import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckIcon, ChevronIcon, EmptyState, Field, InfoIcon, LinkIcon, Modal, PhoneIcon, PlusIcon, Segments, Select, SettingsIcon, Tooltip, useToast } from "@signet/ui";
import { compact, CreatorAvatar, type CreatorPhotos } from "@signet/web-kit";
import type { Stage } from "../../shared/lifecycle.ts";
import { dueToday, groupPhones as groupShared, tickedToday, type PhoneGroup as SharedGroup, type PhoneRow } from "../../shared/phones.ts";
import { instagramProfile } from "../api.ts";
import { createPhone, fetchPosterChoices, savePosters, stockKeys, type Poster, type Stock } from "./api.ts";
import { ended, PhoneCard, restrictedWithLink, sheetLinkMissing, TypeBadge, TypeChip } from "./cards.tsx";
import { useStep } from "./account.tsx";
import { useDayTick } from "./track.tsx";
import { banLine } from "./status.tsx";
import { AccountNoteBox, NoteButton, needsRemoving, PhoneNote, PhoneNoteButton, useRemoved } from "./notes.tsx";
import { PlanButton, PlannedSlots } from "./plan.tsx";
import { openSlots } from "../../shared/phones.ts";

/* ──────────────────────────────────────────────────────────────────────────
   The Accounts tab's phones at three levels (Jay, 2026-10-08), each showing
   only what its question needs:

     All     where does attention go? Every poster, every phone as a small
             tile, every account as a dot in its stage's colour.
     Poster  what is on each of my phones? One box per phone, one line per
             account, today's tick on the line itself.
     Phone   what do I do on this phone now? The full cards (cards.tsx),
             with the previous and next phone a key away.

   The account drawer opens from any of them. Everyone may look at every
   phone; only its poster or a manager may tick or change one (canAct).
   ────────────────────────────────────────────────────────────────────────── */

export type PhoneGroup = SharedGroup<Stock>;
export { dueToday, tickedToday };

export const groupPhones = (accounts: Stock[], today: string, phones?: PhoneRow[]): PhoneGroup[] => groupShared(accounts, today, phones);

// Three states (Jay, 2026-10-08): warming up until the link is in the bio, then active,
// or banned. The same colours on the tiles, the lines and the legend. Since 2026-10-20
// warm-up days are yellow, posting days blue and Not posting orange (Jay), so a phone's
// dots say which half of warming up each account is in.
const TONE: Record<Stage, string> = {
  unassigned: "idle", warmup: "warm", posting: "post", add_link: "post", linked: "live", flagged: "live", highlight: "live", restricted: "live", paused: "paused", ended: "ended",
};
const LEGEND: { tone: string; label: string }[] = [
  { tone: "warm", label: "Warm-up days" },
  { tone: "post", label: "Posting days" },
  { tone: "live", label: "Active" },
  { tone: "paused", label: "Not posting" },
  { tone: "ended", label: "Banned" },
];
const toneOf = (a: Stock) => (ended(a) ? "ended" : TONE[a.step.stage]);
// Link live, as the three-state All view counts it: a bio or highlight link, or restricted with one.
const isLive = (a: Stock) => a.step.stage === "linked" || a.step.stage === "flagged" || a.step.stage === "highlight" || a.step.stage === "restricted";
const typeClass = (t: "aged" | "soft_reg" | "unmarked") => (t === "aged" ? "aged" : t === "unmarked" ? "unmarked" : "soft");

function Dot({ a }: { a: Stock }) {
  return <i className={`igs-dot ${toneOf(a)}`} aria-hidden="true" />;
}

// ── the poster switcher and the phone picker ─────────────────────────────────

export function PosterSwitch({ posters, who, onPick, manages, extra }: { posters: Poster[]; who: string; onPick: (who: string) => void; manages: boolean; extra: Poster | null }) {
  const [editing, setEditing] = useState(false);
  const options = [{ value: "all", label: "All" }, ...posters.map((p) => ({ value: p.id, label: p.name })), ...(extra ? [{ value: extra.id, label: extra.name }] : [])];
  return (
    <>
      <Segments label="Whose phones" value={who} options={options} onChange={onPick} />
      {manages ? (
        <Tooltip content="Choose who is in this list">
          <button type="button" className="icon-button ghost igs-edit-posters" onClick={() => setEditing(true)} aria-label="Choose the posters">
            <SettingsIcon size={15} />
          </button>
        </Tooltip>
      ) : null}
      {editing ? <PostersEditor current={posters} onClose={() => setEditing(false)} /> : null}
    </>
  );
}

function PostersEditor({ current, onClose }: { current: Poster[]; onClose: () => void }) {
  const client = useQueryClient();
  const toast = useToast();
  const choices = useQuery({ queryKey: [...stockKeys.all, "poster-choices"], queryFn: fetchPosterChoices });
  const [picked, setPicked] = useState<string[]>(current.map((p) => p.id));
  const save = useMutation({
    mutationFn: () => savePosters(picked),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: stockKeys.all });
      toast.success("Posters saved");
      onClose();
    },
    onError: (e) => toast.error("Couldn't save the posters", e instanceof Error ? e.message : undefined),
  });
  const people = choices.data?.people ?? [];
  // The chosen keep their order; newly ticked go on the end.
  const toggle = (id: string) => setPicked((now) => (now.includes(id) ? now.filter((x) => x !== id) : [...now, id]));
  return (
    <Modal
      open
      onClose={onClose}
      title="Posters"
      description="Who gets a tab in the switcher. Everyone who can work the Accounts tab can be one; anyone left out still shows under All."
      footer={
        <>
          <button type="button" className="secondary-button" onClick={onClose}>Cancel</button>
          <button type="button" className="button brass" disabled={save.isPending} onClick={() => save.mutate()}>Save</button>
        </>
      }
    >
      <ul className="igs-poster-choices">
        {people.map((p) => {
          const on = picked.includes(p.id);
          return (
            <li key={p.id}>
              <button type="button" className={`igs-poster-choice${on ? " on" : ""}`} aria-pressed={on} onClick={() => toggle(p.id)}>
                <span className="igs-poster-check">{on ? <CheckIcon size={13} /> : null}</span>
                <strong>{p.name}</strong>
                <span>{p.username}</span>
                {on ? <b>{picked.indexOf(p.id) + 1}</b> : null}
              </button>
            </li>
          );
        })}
      </ul>
    </Modal>
  );
}

/**
 * Create phone: an empty phone without assigning an account to make one
 * (Jay, 2026-10-20). A poster makes their own. A manager picks the poster,
 * unless they are a poster too, when it is theirs, as with Assign. Numbers
 * only, so the box takes nothing else.
 */
export function CreatePhone({ posters, who, manages, me, phones, onMade }: {
  posters: Poster[]; who: string; manages: boolean; me: string; phones: PhoneGroup[]; onMade: (poster: string, label: string) => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" className="secondary-button igs-create-phone" onClick={() => setOpen(true)}>
        <PlusIcon size={14} /> Create phone
      </button>
      {open ? <CreatePhoneModal posters={posters} who={who} manages={manages} me={me} phones={phones} onMade={onMade} onClose={() => setOpen(false)} /> : null}
    </>
  );
}

function CreatePhoneModal({ posters, who, manages, me, phones, onMade, onClose }: {
  posters: Poster[]; who: string; manages: boolean; me: string; phones: PhoneGroup[]; onMade: (poster: string, label: string) => void; onClose: () => void;
}) {
  const client = useQueryClient();
  const toast = useToast();
  const pick = manages && !posters.some((p) => p.id === me);
  const [poster, setPoster] = useState<string | null>(pick ? (posters.some((p) => p.id === who) ? who : null) : me);
  const [number, setNumber] = useState("");
  const taken = Boolean(poster && number && phones.some((g) => g.poster === poster && g.label === number));
  const name = pick ? posters.find((p) => p.id === poster)?.name ?? "their" : "your";
  const save = useMutation({
    mutationFn: () => createPhone({ phone: number, ...(pick ? { poster: poster! } : {}) }),
    onSuccess: (made) => {
      void client.invalidateQueries({ queryKey: stockKeys.all });
      toast.success(pick ? `Phone ${made.label} made for ${name}` : `Phone ${made.label} made`, "It takes the type of the first account put on it.");
      onMade(poster!, made.label);
      onClose();
    },
    onError: (e) => toast.error("Couldn't make the phone", e instanceof Error ? e.message : undefined),
  });
  const ready = Boolean(poster && number && !taken && !save.isPending);
  return (
    <Modal
      open
      onClose={onClose}
      title="Create phone"
      description="An empty phone. It becomes aged or soft reg with the first account put on it."
      footer={
        <>
          <button type="button" className="secondary-button" onClick={onClose}>Cancel</button>
          <button type="button" className="button brass" disabled={!ready} onClick={() => save.mutate()}>
            {save.isPending ? "Creating…" : "Create phone"}
          </button>
        </>
      }
    >
      <form
        className="igs-take"
        onSubmit={(e) => {
          e.preventDefault();
          if (ready) save.mutate();
        }}
      >
        {pick ? (
          <Field label="Poster">
            <Select
              label="Poster"
              value={poster}
              options={posters.map((p) => ({ value: p.id, label: p.name }))}
              onChange={setPoster}
              placeholder={posters.length ? "Choose whose phone it is" : "Tick posters with the cog beside the switcher first"}
              searchable
            />
          </Field>
        ) : null}
        <Field label="Phone number" error={taken ? `${pick ? `${name} already has` : "You already have"} phone ${number}.` : undefined}>
          <input
            value={number}
            onChange={(e) => setNumber(e.target.value.replace(/\D/g, "").slice(0, 6))}
            inputMode="numeric"
            pattern="[0-9]*"
            placeholder="e.g. 12"
            autoComplete="off"
            autoFocus
          />
        </Field>
      </form>
    </Modal>
  );
}

export function PhonePicker({ phones, phone, onPick }: { phones: PhoneGroup[]; phone: string | null; onPick: (label: string | null) => void }) {
  const options = [{ value: "all", label: "All phones" }, ...phones.map((g) => ({ value: g.label, label: `Phone ${g.label}` }))];
  return (
    <Select
      label="Phone"
      value={phone ?? "all"}
      options={options}
      onChange={(v) => onPick(!v || v === "all" ? null : v)}
      searchable={phones.length > 8}
      className="igs-phone-picker"
      renderTrigger={() => (
        <span className="igs-phone-picker-value">
          <PhoneIcon size={15} /> Phone: <strong>{phone ?? "All phones"}</strong>
        </span>
      )}
    />
  );
}

// ── All ─────────────────────────────────────────────────────────────────────

export function SystemLevel({ groups, posters, accounts, today, onPoster, onPhone }: {
  groups: PhoneGroup[]; posters: Poster[]; accounts: Stock[]; today: string; onPoster: (id: string) => void; onPhone: (poster: string, label: string) => void;
}) {
  // On a phone = has a phone (imported accounts have no take date).
  const taken = accounts.filter((a) => a.phone);
  const live = taken.filter((a) => !ended(a));
  const weekAgo = new Date(Date.parse(`${today}T00:00:00Z`) - 7 * 86_400_000).toISOString().slice(0, 10);
  const paused = (a: Stock) => a.step.stage === "paused";
  const figures = [
    { n: live.filter((a) => !isLive(a) && !paused(a)).length, label: "Warming up", tone: "warm" },
    { n: live.filter((a) => isLive(a)).length, label: "Active", tone: "live" },
    { n: live.filter(paused).length, label: "Not posting", tone: "paused" },
    { n: taken.filter((a) => ended(a) && (a.statusOn ?? "") >= weekAgo).length, label: "Banned this week", tone: "ended" },
  ];
  // The switcher's posters in its order, then anyone else with phones.
  const order = [...posters.map((p) => ({ id: p.id, name: p.name }))];
  for (const g of groups) if (!order.some((o) => o.id === g.poster)) order.push({ id: g.poster, name: g.posterName });

  return (
    <div className="igs-system">
      <div className="igs-figures">
        {figures.map((f) => (
          <div key={f.label} className={`igs-figure ${f.tone}`}>
            <b className="tabular">{f.n}</b>
            <span>{f.label}</span>
          </div>
        ))}
      </div>
      <ul className="igs-legend" aria-label="What the dots mean">
        {LEGEND.map((l) => (
          <li key={l.tone}><i className={`igs-dot ${l.tone}`} aria-hidden="true" />{l.label}</li>
        ))}
      </ul>
      {order.map((p) => {
        const mine = groups.filter((g) => g.poster === p.id);
        const liveCount = mine.reduce((n, g) => n + g.live.length, 0);
        return (
          <section key={p.id} className="igs-poster-row" aria-label={p.name}>
            <header>
              <button type="button" className="igs-poster-name" onClick={() => onPoster(p.id)}>
                {p.name} <ChevronIcon size={14} direction="right" />
              </button>
              <span className="igs-poster-meta">
                {mine.length} {mine.length === 1 ? "phone" : "phones"}, {liveCount} live {liveCount === 1 ? "account" : "accounts"}
              </span>
            </header>
            {mine.length ? (
              <div className="igs-tiles">
                {mine.map((g) => <Tile key={g.key} g={g} onClick={() => onPhone(g.poster, g.label)} />)}
              </div>
            ) : (
              <p className="igs-faint">No phones yet. Accounts land here once they are taken or assigned from Free to take.</p>
            )}
          </section>
        );
      })}
    </div>
  );
}

function Tile({ g, onClick }: { g: PhoneGroup; onClick: () => void }) {
  const tip = (
    <ul className="igs-tip">
      {g.accounts.map((a) => (
        <li key={a.id}><Dot a={a} />@{a.username}<span>{a.step.title}</span></li>
      ))}
    </ul>
  );
  return (
    <Tooltip content={tip}>
      <button type="button" className={`igs-tile ${typeClass(g.type)}`} onClick={onClick} aria-label={`Phone ${g.label}: ${g.live.length} live`}>
        <span className="igs-tile-top">
          <span className="igs-tile-label">P{g.label}</span>
          <TypeChip type={g.type} />
        </span>
        <span className="igs-tile-dots">
          {g.accounts.map((a) => <Dot key={a.id} a={a} />)}
          {/* Planned slots nobody has filled yet, as hollow dots (Ben, 2026-10-28). */}
          {openSlots(g.plan, g.live).map((name, i) => <span key={`plan-${i}`} className="igs-dot planned" title={`Planned: ${name}`} />)}
        </span>
      </button>
    </Tooltip>
  );
}

// ── Poster ──────────────────────────────────────────────────────────────────

// No "due" anywhere in IG Suicide: it is not a word the team uses (Jay, 2026-10-08).
type Filter = "all" | "aged" | "soft_reg";

export function PosterLevel({ phones, today, photos, canAct, manages = false, onPhone, onOpen, onLink }: {
  phones: PhoneGroup[]; today: string; photos: CreatorPhotos; canAct: (a: Pick<Stock, "poster">) => boolean; manages?: boolean; onPhone: (label: string) => void; onOpen: (id: string) => void; onLink: (id: string) => void;
}) {
  const [filter, setFilter] = useState<Filter>("all");
  const shown = phones.filter((g) => filter === "all" || g.type === filter);
  if (!phones.length) {
    return <EmptyState icon={<PhoneIcon size={18} />} title="No phones yet" body="Accounts land here once they are taken or assigned from Free to take." />;
  }
  return (
    <div className="igs-poster-level">
      <Segments
        label="Show"
        value={filter}
        options={[
          { value: "all", label: `All ${phones.length}` },
          { value: "aged", label: "Aged" },
          { value: "soft_reg", label: "Soft reg" },
        ]}
        onChange={setFilter}
      />
      {shown.length ? (
        <div className="igs-boxes">
          {shown.map((g) => <PhoneBox key={g.key} g={g} today={today} photos={photos} canAct={canAct} manages={manages} onPhone={() => onPhone(g.label)} onOpen={onOpen} onLink={onLink} />)}
        </div>
      ) : (
        <p className="igs-faint">No phones of that type.</p>
      )}
    </div>
  );
}

function PhoneBox({ g, today, photos, canAct, manages, onPhone, onOpen, onLink }: {
  g: PhoneGroup; today: string; photos: CreatorPhotos; canAct: (a: Pick<Stock, "poster">) => boolean; manages: boolean; onPhone: () => void; onOpen: (id: string) => void; onLink: (id: string) => void;
}) {
  const planned = openSlots(g.plan, g.live).length;
  const gone = g.accounts.filter(ended);
  const [noting, setNoting] = useState(false);
  const mayNote = canAct({ poster: g.poster });
  return (
    <section className={`igs-box ${typeClass(g.type)}`} aria-label={`Phone ${g.label}`}>
      {/* The whole header opens the phone, through a button laid under it; the note button sits on top
          of that one, beside the chip, since a button can't hold another (Jay, 2026-10-22). */}
      <div className="igs-box-head">
        <button type="button" className="igs-box-open" onClick={onPhone} aria-label={`Open phone ${g.label}`} />
        <span className="igs-box-title">Phone <b>{g.label}</b></span>
        <TypeBadge type={g.type} />
        {mayNote ? <PhoneNoteButton phone={g.label} note={g.note} onClick={() => setNoting(true)} /> : null}
        <ChevronIcon size={15} direction="right" className="igs-box-go" />
      </div>
      <PhoneNote poster={g.poster} phone={g.label} note={g.note} canEdit={mayNote} editing={noting} onEditing={setNoting} />
      {g.live.length || gone.length ? (
        <ul className="igs-rows">
          {[...g.live, ...gone].map((a) => <AccountRow key={a.id} a={a} today={today} photos={photos} canAct={canAct(a)} onOpen={() => onOpen(a.id)} onLink={() => onLink(a.id)} />)}
        </ul>
      ) : null}
      <PlannedSlots g={g} photos={photos} />
      {!g.live.length && !planned ? <p className="igs-faint igs-box-empty">No live accounts on this phone.</p> : null}
      {manages ? <PlanButton g={g} photos={photos} /> : null}
    </section>
  );
}

function AccountRow({ a, today, photos, canAct, onOpen, onLink }: { a: Stock; today: string; photos: CreatorPhotos; canAct: boolean; onOpen: () => void; onLink: () => void }) {
  const [noting, setNoting] = useState(false);
  return (
    <li className={`igs-row${ended(a) ? " banned" : dueToday(a, today) ? " due" : ""}`}>
      {/* The line opens the account on Instagram; only the info button opens its drawer (Jay, 2026-10-27). */}
      <a className="igs-row-main" href={instagramProfile(a.username)} target="_blank" rel="noreferrer noopener" aria-label={`Open @${a.username} on Instagram`}>
        {a.creator ? <CreatorAvatar name={a.creator} src={photos(a.creator)} size={36} /> : <span className="igs-row-blank" aria-hidden="true" />}
        <span className="igs-row-copy">
          <strong>@{a.username}</strong>
          {/* Whose account it is, small and grey under the name (Jay, 2026-10-27). */}
          {/* Followers sit beside the creator so each phone shows how its accounts are growing (Ben, 2026-11-02). */}
          {a.creator || a.followers !== null ? (
            <small className="igs-row-creator">
              {a.creator}
              {a.followers !== null ? <b className="igs-row-followers">{compact(a.followers)} followers</b> : null}
            </small>
          ) : null}
          {!ended(a) ? <span><Dot a={a} />{a.missingSince ? "Instagram can't find it" : a.step.title}</span> : null}
          {!ended(a) && a.note ? <em className="igs-row-note">{a.note}</em> : null}
        </span>
      </a>
      <button type="button" className="icon-button ghost small igs-row-info" onClick={onOpen} aria-label={`Details for @${a.username}`} title="Details">
        <InfoIcon size={16} />
      </button>
      {canAct ? <NoteButton label={`@${a.username}`} note={a.note} onClick={() => setNoting(true)} /> : null}
      {canAct && !ended(a) ? <RowAction a={a} today={today} onLink={onLink} /> : null}
      {canAct && needsRemoving(a) ? <RemovedButton a={a} /> : null}
      {/* A ban's line goes under the row at full width: beside the photo it was squeezed into three
          broken lines by the buttons (Ben, 2026-11-02: "maybe below would be better"). */}
      {ended(a) ? (
        <div className="igs-row-ban">
          <span><Dot a={a} />{banLine(a)}</span>
          {needsRemoving(a) ? <em className="igs-row-remove">Log it out of the phone</em> : a.note ? <em className="igs-row-note">{a.note}</em> : null}
        </div>
      ) : null}
      {noting ? <AccountNoteBox a={a} onDone={() => setNoting(false)} /> : null}
    </li>
  );
}

function RowAction({ a, today, onLink }: { a: Stock; today: string; onLink: () => void }) {
  const tick = useDayTick(a);
  const daily = a.step.stage === "warmup" || a.step.stage === "posting";
  if (daily && tickedToday(a, today)) {
    return (
      <span className="igs-row-done">
        <CheckIcon size={14} /> Done today
      </span>
    );
  }
  if (daily) {
    return (
      <button type="button" className="igs-row-tick" disabled={tick.isPending} onClick={() => tick.mutate()} aria-label={`Tick ${a.step.title} for @${a.username}`}>
        <span className="igs-row-box" aria-hidden="true" /> Tick
      </button>
    );
  }
  if (a.step.stage === "add_link" || sheetLinkMissing(a)) {
    return (
      <button type="button" className="secondary-button igs-row-link" onClick={onLink}>
        <LinkIcon size={14} /> Add link
      </button>
    );
  }
  if (a.step.stage === "flagged") return <HighlightButton a={a} />;
  if (a.step.stage === "highlight" || restrictedWithLink(a)) {
    return (
      <button type="button" className="secondary-button igs-row-link" onClick={onLink} title="The account is fine again: give it a new link in the bio">
        <LinkIcon size={14} /> New link
      </button>
    );
  }
  return null;
}

function RemovedButton({ a }: { a: Stock }) {
  const removed = useRemoved();
  return (
    <button type="button" className="button danger igs-row-link" disabled={removed.isPending} onClick={() => removed.mutate({ id: a.id, removed: true })} aria-label={`@${a.username} is logged out of the phone`}>
      Removed from phone
    </button>
  );
}

function HighlightButton({ a }: { a: Stock }) {
  const step = useStep();
  return (
    <button type="button" className="secondary-button igs-row-link" disabled={step.isPending} onClick={() => step.mutate({ id: a.id, step: "highlight" })}>
      Moved to highlight
    </button>
  );
}

// ── Phone ───────────────────────────────────────────────────────────────────

export function PhoneLevel({ phones, phone, photos, canAct, manages = false, onPhone, onOpen, onLink }: {
  phones: PhoneGroup[]; phone: string; photos: CreatorPhotos; canAct: (a: Pick<Stock, "poster">) => boolean; manages?: boolean; onPhone: (label: string) => void; onOpen: (id: string) => void; onLink: (id: string) => void;
}) {
  const index = phones.findIndex((g) => g.label === phone);
  const g = phones[index];
  const prev = index > 0 ? phones[index - 1] : null;
  const next = index >= 0 && index < phones.length - 1 ? phones[index + 1] : null;

  // [ and ] step through the phones, for working down the pile of phones in hand.
  const keys = useMemo(() => ({ prev: prev?.label ?? null, next: next?.label ?? null }), [prev, next]);
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (target?.closest("input, textarea, [contenteditable='true'], [role='dialog']")) return;
      if (event.key === "[" && keys.prev) onPhone(keys.prev);
      if (event.key === "]" && keys.next) onPhone(keys.next);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [keys, onPhone]);

  if (!g) return <EmptyState icon={<PhoneIcon size={18} />} title={`No phone ${phone} here`} body="Pick another phone above." />;
  return (
    <div className="igs-phone-level">
      <div className="igs-phone-nav">
        <button type="button" className="secondary-button" disabled={!prev} onClick={() => prev && onPhone(prev.label)} aria-label={prev ? `Previous: phone ${prev.label}` : "No previous phone"}>
          <ChevronIcon size={14} direction="left" /> {prev ? `Phone ${prev.label}` : "Previous"}
        </button>
        <span className="igs-faint">Phone {index + 1} of {phones.length}. Press [ or ] to move.</span>
        <button type="button" className="secondary-button" disabled={!next} onClick={() => next && onPhone(next.label)} aria-label={next ? `Next: phone ${next.label}` : "No next phone"}>
          {next ? `Phone ${next.label}` : "Next"} <ChevronIcon size={14} direction="right" />
        </button>
      </div>
      <PhoneCard label={g.label} type={g.type} poster={g.poster} owner={g.posterName} note={g.note} plan={g.plan} manages={manages} accounts={g.accounts} photos={photos} onOpen={onOpen} onLink={onLink} showPoster canAct={canAct} />
    </div>
  );
}
